import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { ObjectWorkspace } from '../scientific-objects/workspace/ObjectWorkspace';
import { AppChrome } from './AppChrome';
import { ProfilePlaceholder } from '../components/ProfilePlaceholder';
import { WorkspaceShell } from './workspace/WorkspaceShell';
import { DragDropProvider } from '../dnd/DragDrop';
import { LegacyPublicDrawingRedirect, PublicDrawingPage } from '../features/public/PublicDrawingPage';
import { PublicReactionPage } from '../features/public/PublicReactionPage';
import { BondGlossaryPage, BondTermPage } from '../features/public/BondGlossaryPage';
import { PublicHome } from '../features/public/PublicHome';
import { LegacyPublicMoleculeRedirect, PublicMoleculePage } from '../features/public/PublicMoleculePage';
import { MyCommentsPage } from '../features/discussions/MyCommentsPage';
import { OrganisationsPage } from '../tenancy/OrganisationsPage';
import { SwaggerPage } from '../features/swagger/SwaggerPage';

function MoleculeObjectRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/" replace />;
  return <ObjectWorkspace reference={{ type: 'MOLECULE', id }} />;
}

function ProteinObjectRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/" replace />;
  return <ObjectWorkspace reference={{ type: 'PROTEIN', id }} />;
}

function GeneObjectRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/" replace />;
  return <ObjectWorkspace reference={{ type: 'GENE', id }} />;
}

function VariantObjectRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/" replace />;
  return <ObjectWorkspace reference={{ type: 'VARIANT', id }} />;
}
function TranscriptObjectRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/" replace />;
  return <ObjectWorkspace reference={{ type: 'TRANSCRIPT', id }} />;
}

function PeptideObjectRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) return <Navigate to="/" replace />;
  return <ObjectWorkspace reference={{ type: 'PEPTIDE', id }} />;
}

export function AppRouter() {
  return (
    <Routes>
      <Route element={<AppChrome />}>
        <Route path="/" element={<ProfilePlaceholder />} />
        {/* ADR 0016 (#895): your own comments, wherever they are. */}
        <Route path="/me/comments" element={<MyCommentsPage />} />
        {/* #1051: your organisations, their members, invitations and settings. */}
        <Route path="/me/organisations" element={<OrganisationsPage />} />
      </Route>
      {/* ADR 0007 P10e: /draw is the redesigned workspace shell. It carries its own top bar,
          so it renders outside AppChrome. #699: drag and drop works across the whole workspace. */}
      <Route
        path="/draw"
        element={
          <DragDropProvider>
            <WorkspaceShell />
          </DragDropProvider>
        }
      />
      {/* #885: the public catalog, the same pages signed-out visitors see (shared links work both ways). */}
      <Route path="/public" element={<PublicHome />} />
      <Route path="/i/:id" element={<PublicDrawingPage />} />
      {/* #1124 (ADR 0023): a public reaction. */}
      <Route path="/r/:id" element={<PublicReactionPage />} />
      {/* ADR 0023 D7 (#1126): the public bond glossary. */}
      <Route path="/bonds" element={<BondGlossaryPage />} />
      <Route path="/bonds/:slug" element={<BondTermPage />} />
      <Route path="/public/drawings/:id" element={<LegacyPublicDrawingRedirect />} />
      <Route path="/m/:id" element={<PublicMoleculePage />} />
      <Route path="/public/molecules/:id" element={<LegacyPublicMoleculeRedirect />} />
      <Route path="/objects/molecule/:id" element={<MoleculeObjectRoute />} />
      <Route path="/objects/protein/:id" element={<ProteinObjectRoute />} />
      <Route path="/objects/gene/:id" element={<GeneObjectRoute />} />
      <Route path="/objects/variant/:id" element={<VariantObjectRoute />} />
      <Route path="/objects/transcript/:id" element={<TranscriptObjectRoute />} />
      <Route path="/objects/peptide/:id" element={<PeptideObjectRoute />} />
      {/* Swagger UI API Explorer - authenticated users only */}
      <Route path="/swagger" element={<SwaggerPage />} />
      <Route path="/swagger/*" element={<SwaggerPage />} />
      <Route path="/swagger-ui.html" element={<Navigate to="/swagger" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
